/**
 * Single source of truth for the donation lifecycle on the frontend.
 *
 * Mirrors the backend App\Support\DonationLifecycle. Every portal (employee, employer,
 * charity, admin) — every list, badge and the progress stepper — derives its status label and
 * colour from here, so wording and colour can never drift between screens.
 *
 * Canonical forward progression:
 *   submitted -> approved -> employer_paid -> paid_to_charity -> received_by_charity
 * Plus the terminal branch `rejected`, and the pre-flow `awaiting_employer_registration`.
 *
 * Note: "Paid by employer" and "Received by L2G" are currently a single tracked milestone
 * (employer_paid). If/when an explicit L2G-receipt confirmation is tracked, split it here and
 * in the backend helper — every screen updates automatically.
 */

export type LifecycleStage =
  | 'submitted'
  | 'approved'
  | 'employer_paid'
  | 'paid_to_charity'
  | 'received_by_charity'
  | 'rejected'
  | 'awaiting_employer_registration';

// Kept for back-compat with existing call sites; labels are now audience-neutral (one canonical
// set) so the same status reads identically in every portal.
export type LifecycleAudience = 'employee' | 'employer' | 'charity' | 'admin';

/** Distinct colour tone per stage — a logical progression, every stage a different colour. */
export type LifecycleTone = 'yellow' | 'orange' | 'amber' | 'blue' | 'cyan' | 'teal' | 'green' | 'red' | 'grey';

/**
 * Single source of truth for every status colour in the app. Each tone has a solid colour
 * (stepper markers / dots), and a soft background + text pair (badges/pills).
 *
 * Every status surface — the lifecycle badges (lc-tone-* in app.css), the Stepper, the legacy
 * StatusBadge / PayoutStatusBadge, and the employer payment-report badges — must draw from
 * these exact values so the same semantic colour is identical everywhere. The lc-tone-* classes
 * in src/app.css mirror the bg/text values below; keep them in sync.
 */
export const TONE_PALETTE: Record<LifecycleTone, { solid: string; bg: string; text: string }> = {
  yellow: { solid: '#eab308', bg: '#fef9c3', text: '#854d0e' },
  orange: { solid: '#f97316', bg: '#ffedd5', text: '#9a3412' },
  amber:  { solid: '#f59e0b', bg: '#fef3c7', text: '#92400e' },
  blue:   { solid: '#3b82f6', bg: '#dbeafe', text: '#1e40af' },
  cyan:   { solid: '#06b6d4', bg: '#cffafe', text: '#155e75' },
  teal:   { solid: '#14b8a6', bg: '#ccfbf1', text: '#0f766e' },
  green:  { solid: '#22c55e', bg: '#d1fae5', text: '#065f46' },
  red:    { solid: '#ea4335', bg: '#fee2e2', text: '#991b1b' },
  grey:   { solid: '#9ca3af', bg: '#f3f4f6', text: '#374151' },
};

/** Ordered forward stages (rejected/awaiting are not part of the forward order). */
export const FORWARD_STAGES: LifecycleStage[] = [
  'submitted',
  'approved',
  'employer_paid',
  'paid_to_charity',
  'received_by_charity',
];

/** Canonical, single label set — used by lists, badges AND the stepper so they always line up. */
export const STAGE_LABEL: Record<LifecycleStage, string> = {
  submitted: 'Submitted to Employer',
  approved: 'Approved by Employer',
  employer_paid: 'Paid by Employer',
  paid_to_charity: 'Paid to Charity',
  received_by_charity: 'Received by Charity',
  rejected: 'Rejected by Employer',
  awaiting_employer_registration: 'Awaiting Employer Registration',
};

/**
 * Distinct colour per stage. The forward path runs yellow → orange → cyan → teal → green
 * (a warm-to-cool ramp into success-green); rejected is red; the pre-flow awaiting
 * state is neutral grey.
 */
export const STAGE_TONE: Record<LifecycleStage, LifecycleTone> = {
  submitted: 'yellow',
  approved: 'orange',
  employer_paid: 'cyan',
  paid_to_charity: 'teal',
  received_by_charity: 'green',
  rejected: 'red',
  awaiting_employer_registration: 'grey',
};

/** Step labels for the stepper — same canonical text as everywhere else. */
export const STEP_LABELS: Record<LifecycleStage, string> = STAGE_LABEL;

export interface StageMeta {
  stage: LifecycleStage;
  label: string;
  tone: LifecycleTone;
  /** Global CSS class for the badge colour (defined once in app.css). */
  badgeClass: string;
  order: number;
}

/**
 * Resolve the label + colour for a stage. `audience` is accepted for back-compat but ignored —
 * labels are now a single canonical set shared by every portal.
 */
export function getStageMeta(stage: LifecycleStage, _audience?: LifecycleAudience): StageMeta {
  const tone = STAGE_TONE[stage];
  return {
    stage,
    label: STAGE_LABEL[stage],
    tone,
    badgeClass: `lc-tone-${tone}`,
    order: FORWARD_STAGES.indexOf(stage),
  };
}

export interface StepperItem {
  key: LifecycleStage;
  label: string;
  tone: LifecycleTone;
  state: 'done' | 'current' | 'upcoming' | 'cancelled';
}

/**
 * Build the progress-stepper steps for a donation at `current` stage.
 * When rejected, show submitted then rejected — rejection is a terminal branch from submission,
 * not a step after approval.
 */
export function getStepper(current: LifecycleStage): StepperItem[] {
  if (current === 'rejected') {
    return [
      { key: 'submitted', label: STEP_LABELS.submitted, tone: STAGE_TONE.submitted, state: 'done' },
      { key: 'rejected', label: STEP_LABELS.rejected, tone: STAGE_TONE.rejected, state: 'cancelled' },
    ];
  }

  if (current === 'awaiting_employer_registration') {
    // Parked before the normal flow until the employer registers.
    return [
      {
        key: 'awaiting_employer_registration',
        label: STEP_LABELS.awaiting_employer_registration,
        tone: STAGE_TONE.awaiting_employer_registration,
        state: 'current',
      },
      ...FORWARD_STAGES.map((key) => ({
        key,
        label: STEP_LABELS[key],
        tone: STAGE_TONE[key],
        state: 'upcoming' as const,
      })),
    ];
  }

  const currentIndex = FORWARD_STAGES.indexOf(current);
  // The last forward stage (received_by_charity) is terminal/complete — tick every node.
  const isComplete = currentIndex === FORWARD_STAGES.length - 1;
  return FORWARD_STAGES.map((key, index) => ({
    key,
    label: STEP_LABELS[key],
    tone: STAGE_TONE[key],
    state: isComplete || index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'upcoming',
  }));
}

/**
 * Best-effort mapping from a raw donation_request_status name to a lifecycle stage, for
 * components that only have the status string. Components with the backend-resolved `stage`
 * field should use that directly — it is more precise (it accounts for payouts/receipts).
 */
export function stageFromStatusName(name: string | null | undefined): LifecycleStage {
  const n = (name ?? '').toLowerCase();
  if (n.includes('awaiting')) return 'awaiting_employer_registration';
  switch (n) {
    case 'approved':
      return 'approved';
    case 'rejected':
    case 'declined':
      return 'rejected';
    case 'completed':
    case 'processed':
      return 'received_by_charity';
    case 'pending':
    default:
      return 'submitted';
  }
}

/** Convenience: label straight from a raw status name. */
export function labelFromStatusName(name: string | null | undefined): string {
  return getStageMeta(stageFromStatusName(name)).label;
}

/** Employee donations page — simplified status buckets for summary tiles and table filters. */
export type DonationStatusBucket = 'requested' | 'approved' | 'rejected';

export const STATUS_BUCKET_LABEL: Record<DonationStatusBucket, string> = {
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
};

/** Comma-separated lifecycle_stage values sent to the API when a bucket filter is active. */
export const STATUS_BUCKET_LIFECYCLE_STAGES: Record<DonationStatusBucket, string> = {
  requested: 'submitted,awaiting_employer_registration',
  approved: 'approved,employer_paid,paid_to_charity,received_by_charity',
  rejected: 'rejected',
};

/** Lifecycle stages that count as approved for displaying leave value. */
export const APPROVED_VALUE_STAGES: LifecycleStage[] = [
  'approved',
  'employer_paid',
  'paid_to_charity',
  'received_by_charity',
];

export function isApprovedValueStage(stage: LifecycleStage | string | null | undefined): boolean {
  return APPROVED_VALUE_STAGES.includes(stage as LifecycleStage);
}

/** Map a lifecycle stage to the simplified employee status bucket. */
export function stageToStatusBucket(stage: LifecycleStage): DonationStatusBucket {
  if (stage === 'rejected') return 'rejected';
  if (stage === 'submitted' || stage === 'awaiting_employer_registration') return 'requested';
  return 'approved';
}

export const STATUS_BUCKET_TONE: Record<DonationStatusBucket, LifecycleTone> = {
  requested: 'yellow',
  approved: 'green',
  rejected: 'red',
};

export function getStatusBucketMeta(stage: LifecycleStage): {
  bucket: DonationStatusBucket;
  label: string;
  tone: LifecycleTone;
  badgeClass: string;
} {
  const bucket = stageToStatusBucket(stage);
  const tone = STATUS_BUCKET_TONE[bucket];
  return {
    bucket,
    label: STATUS_BUCKET_LABEL[bucket],
    tone,
    badgeClass: `lc-tone-${tone}`,
  };
}

function statusBucketIconSvg(bucket: DonationStatusBucket): string {
  if (bucket === 'rejected') {
    return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
  }
  if (bucket === 'requested') {
    return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';
  }
  return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>';
}

/** HTML for the simplified Requested / Approved / Rejected badge used on employee lists. */
export function statusBucketBadgeHtml(stage: LifecycleStage): string {
  const meta = getStatusBucketMeta(stage);
  return `<span class="lc-badge ${meta.badgeClass}">${statusBucketIconSvg(meta.bucket)}<span>${meta.label}</span></span>`;
}

export function statusBucketBadgeHtmlFromRow(row: {
  lifecycle_stage?: string;
  status?: string;
}): string {
  const stage = (row.lifecycle_stage as LifecycleStage) || stageFromStatusName(row.status);
  return statusBucketBadgeHtml(stage);
}
